// ============================================
// CANDIDATE DETAIL PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { createPortal } from 'react-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Briefcase, 
  Star, 
  Calendar, 
  Award, 
  Ship, 
  Heart, 
  Mail, 
  FileText, 
  Edit2,
  CheckCircle,
  Clock,
  AlertCircle,
  Anchor,
  Globe,
  User,
  BadgeCheck,
  Loader2,
  X,
} from 'lucide-react';
import { FavoriteNoteModal } from '@/components';
import { 
  useSeafarerProfile, 
  useFavoriteCandidates, 
  useAddFavoriteCandidate, 
  useRemoveFavoriteCandidate 
} from '@/hooks';
import { formatDate, cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/candidates/$id')({
  component: CandidateDetailPage,
});

function CandidateDetailPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const { id } = Route.useParams();
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [isPhotoEnlarged, setIsPhotoEnlarged] = useState(false);
  
  const { data: rawProfileData, isLoading, error } = useSeafarerProfile(id);
  const { data: rawFavoritesData } = useFavoriteCandidates();
  const addFavoriteMutation = useAddFavoriteCandidate();
  const removeFavoriteMutation = useRemoveFavoriteCandidate();

  // Handle API response wrapper - nested { success, data: {...} }
  const profile = (() => {
    if (!rawProfileData) return null;
    const raw = rawProfileData as any;
    // { success, data: { ...profile } }
    if (raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) && raw.data.id) return raw.data;
    if (raw.id) return raw;
    return raw.data || raw;
  })();
  const favoritesArray = (() => {
    if (!rawFavoritesData) return [];
    const raw = rawFavoritesData as any;
    // { success, data: [...] }
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.items)) return raw.items;
    return [];
  })();
  
  const favoriteEntry = favoritesArray.find((f: any) => f.seafarer?.id === id);
  const isFavorite = !!favoriteEntry;

  const toggleFavorite = async () => {
    if (isFavorite) {
      await removeFavoriteMutation.mutateAsync(id);
    } else {
      await addFavoriteMutation.mutateAsync({ seafarerId: id });
    }
  };

  const handleContact = () => {
    // TODO: İletişim modal veya mesajlaşma sistemi
    alert(t('panel:candidates.detail.contactSoon'));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-maritime-500 mx-auto mb-4" />
          <p className="text-gray-600">{t('panel:candidates.detail.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6">
        <Link 
          to="/candidates" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-maritime-600"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('panel:candidates.detail.backToCandidates')}
        </Link>
        <div className="bg-white rounded-maritime border border-gray-200 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('panel:candidates.detail.notFoundTitle')}</h2>
          <p className="text-gray-600 mb-4">
            {t('panel:candidates.detail.notFoundDesc')}
          </p>
          <Link to="/candidates" className="btn-primary">
            {t('panel:candidates.detail.backToCandidates')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Geri bağlantı */}
      <Link 
        to="/candidates" 
        className="inline-flex items-center gap-2 text-gray-600 hover:text-maritime-600"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('panel:candidates.detail.backToCandidates')}
      </Link>

      {/* Profil Başlık */}
      <div className="bg-white rounded-maritime border border-gray-200 overflow-hidden">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-maritime-500 to-maritime-700" />
        
        {/* Profil Bilgileri */}
        <div className="px-6 pb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Avatar */}
            <div className="-mt-12 w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden flex-shrink-0 relative group cursor-pointer" onClick={() => profile.avatarUrl && setIsPhotoEnlarged(true)}>
              {profile.avatarUrl ? (
                <>
                  <img 
                    src={profile.avatarUrl} 
                    alt={`${profile.firstName} ${profile.lastName}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                </>
              ) : (
                <div className="w-full h-full bg-maritime-100 flex items-center justify-center text-2xl font-bold text-maritime-600">
                  {profile.firstName?.[0]}{profile.lastName?.[0]}
                </div>
              )}
            </div>

            {/* Ad & Ünvan */}
            <div className="flex-1 pt-2 md:pt-3">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">
                  {profile.firstName} {profile.lastName}
                </h1>
                {profile.isVerified && (
                  <span title={t('panel:candidates.detail.verifiedProfile')}>
                    <BadgeCheck className="w-6 h-6 text-blue-500" />
                  </span>
                )}
              </div>
              <p className="text-lg text-maritime-600">
                {profile.currentRank?.name || profile.currentRank?.nameEn || t('panel:candidates.detail.noPosition')}
              </p>
            </div>

            {/* Aksiyon Butonları */}
            <div className="flex items-center gap-3 pt-2 md:pt-3">
              <button
                onClick={toggleFavorite}
                disabled={addFavoriteMutation.isPending || removeFavoriteMutation.isPending}
                className={cn(
                  'btn-secondary inline-flex items-center gap-2',
                  isFavorite && 'text-red-600 border-red-200 bg-red-50'
                )}
              >
                {isFavorite ? (
                  <>
                    <Heart className="w-4 h-4 fill-current" />
                    {t('panel:candidates.detail.inFavorites')}
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4" />
                    {t('panel:candidates.detail.addToFavorites')}
                  </>
                )}
              </button>
              {isFavorite && (
                <button
                  onClick={() => setShowNoteModal(true)}
                  className="btn-secondary inline-flex items-center gap-2"
                  title={t('panel:candidates.detail.editNote')}
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              <button onClick={handleContact} className="btn-primary inline-flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {t('panel:candidates.detail.contactNow')}
              </button>
            </div>
          </div>

          {/* Favori notları göster */}
          {isFavorite && (favoriteEntry?.notes || favoriteEntry?.tags?.length > 0) && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-100">
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800 mb-1">{t('panel:candidates.detail.yourNotes')}</p>
                  {favoriteEntry.notes && (
                    <p className="text-sm text-yellow-700 mb-2">{favoriteEntry.notes}</p>
                  )}
                  {Array.isArray(favoriteEntry.tags) && favoriteEntry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {favoriteEntry.tags.map((tag: string) => (
                        <span 
                          key={tag}
                          className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => setShowNoteModal(true)}
                  className="text-yellow-600 hover:text-yellow-700"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ana İçerik */}
        <div className="lg:col-span-2 space-y-6">
          {/* Özet Kartları */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-maritime border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Briefcase className="w-4 h-4" />
                <span className="text-sm">{t('panel:candidates.detail.summary.department')}</span>
              </div>
              <p className="font-semibold text-gray-900">
                {t(`enums:department.${profile.department}`, profile.department)}
              </p>
            </div>
            <div className="bg-white rounded-maritime border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Star className="w-4 h-4" />
                <span className="text-sm">{t('panel:candidates.detail.summary.experience')}</span>
              </div>
              <p className="font-semibold text-gray-900">{t('panel:candidates.detail.summary.years', { count: profile.experienceYears || 0 })}</p>
            </div>
            <div className="bg-white rounded-maritime border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Ship className="w-4 h-4" />
                <span className="text-sm">{t('panel:candidates.detail.summary.seaDays')}</span>
              </div>
              <p className="font-semibold text-gray-900">{t('panel:candidates.detail.summary.days', { count: profile.totalSeaDays || 0 })}</p>
            </div>
            <div className="bg-white rounded-maritime border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">{t('panel:candidates.detail.summary.availability')}</span>
              </div>
              <p className="font-semibold text-gray-900">
                {profile.availableFrom 
                  ? formatDate(profile.availableFrom, 'dd MMM yyyy')
                  : t('panel:candidates.detail.summary.notSpecified')
                }
              </p>
            </div>
          </div>

          {/* Hakkında */}
          {profile.bio && (
            <div className="bg-white rounded-maritime border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-maritime-500" />
                {t('panel:candidates.detail.about')}
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{profile.bio}</p>
            </div>
          )}

          {/* Tercih Edilen Gemi Tipleri */}
          {Array.isArray(profile.preferredVesselTypes) && profile.preferredVesselTypes.length > 0 && (
            <div className="bg-white rounded-maritime border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Anchor className="w-5 h-5 text-maritime-500" />
                {t('panel:candidates.detail.preferredVesselTypes')}
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.preferredVesselTypes.map((type: string) => (
                  <span 
                    key={type}
                    className="px-3 py-1.5 bg-maritime-50 text-maritime-700 rounded-full text-sm"
                  >
                    {t(`enums:vesselType.${type}`, type)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sertifikalar */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-maritime-500" />
              {t('panel:candidates.detail.certificates.title')}
            </h2>
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {t('panel:candidates.detail.certificates.sharedInfo')}
            </div>
          </div>

          {/* Deniz Hizmeti */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Ship className="w-5 h-5 text-maritime-500" />
              {t('panel:candidates.detail.seaService.title')}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">{t('panel:candidates.detail.seaService.totalSeaDays')}</p>
                <p className="text-2xl font-bold text-gray-900">{profile.totalSeaDays || 0}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">{t('panel:candidates.detail.seaService.experience')}</p>
                <p className="text-2xl font-bold text-gray-900">{t('panel:candidates.detail.summary.years', { count: profile.experienceYears || 0 })}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* İletişim & Bilgi Kartı */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:candidates.detail.info.title')}</h2>
            <div className="space-y-4">
              {/* Uyruk */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('panel:candidates.detail.info.nationality')}</p>
                  <p className="font-medium text-gray-900">{profile.nationality || t('panel:candidates.detail.summary.notSpecified')}</p>
                </div>
              </div>

              {/* Lokasyon */}
              {(profile.city || profile.country) && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('panel:candidates.detail.info.location')}</p>
                    <p className="font-medium text-gray-900">
                      {[profile.city, profile.country].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Doğrulama Durumu */}
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  profile.isVerified ? 'bg-green-100' : 'bg-gray-100'
                )}>
                  {profile.isVerified ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('panel:candidates.detail.info.verificationStatus')}</p>
                  <p className={cn(
                    'font-medium',
                    profile.isVerified ? 'text-green-600' : 'text-gray-600'
                  )}>
                    {profile.isVerified ? t('panel:candidates.detail.info.verified') : t('panel:candidates.detail.info.unverified')}
                  </p>
                </div>
              </div>

              {/* Min Maaş Beklentisi */}
              {profile.minSalaryExpectation && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('panel:candidates.detail.info.minSalary')}</p>
                    <p className="font-medium text-gray-900">
                      ${profile.minSalaryExpectation.toLocaleString()}{t('panel:candidates.detail.info.perMonth')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* İletişim Butonu */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button 
                onClick={handleContact}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {t('panel:candidates.detail.contactNow')}
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                {t('panel:candidates.detail.contactInfo')}
              </p>
            </div>
          </div>

          {/* Hızlı İşlemler */}
          <div className="bg-white rounded-maritime border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:candidates.detail.quickActions.title')}</h2>
            <div className="space-y-2">
              <button
                onClick={toggleFavorite}
                disabled={addFavoriteMutation.isPending || removeFavoriteMutation.isPending}
                className={cn(
                  'w-full btn-secondary inline-flex items-center justify-center gap-2',
                  isFavorite && 'text-red-600 border-red-200 bg-red-50'
                )}
              >
                <Heart className={cn('w-4 h-4', isFavorite && 'fill-current')} />
                {isFavorite ? t('panel:candidates.detail.removeFromFavorites') : t('panel:candidates.detail.addToFavorites')}
              </button>
              {isFavorite && (
                <button
                  onClick={() => setShowNoteModal(true)}
                  className="w-full btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  {t('panel:candidates.detail.addEditNote')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Not Modal */}
      <FavoriteNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        seafarerId={id}
        seafarerName={`${profile.firstName} ${profile.lastName}`}
        initialNotes={favoriteEntry?.notes || ''}
        initialTags={favoriteEntry?.tags || []}
      />
      {/* Enlarge Photo Modal */}
      {isPhotoEnlarged && profile.avatarUrl && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setIsPhotoEnlarged(false)}
        >
          <button 
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsPhotoEnlarged(false);
            }}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={profile.avatarUrl} 
            alt={`${profile.firstName} ${profile.lastName}`}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
